import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../features/auth/authSlice";

// Resolves the permission row for the CURRENT page from the sidebar tree the
// user got at login, for pages whose routes carry ids (…/create-fund-request/12,
// …/expense-punch/create-expense-punch/7?type=view) which findMatchingPath's
// exact-path lookup cannot see. A tree node matches when its own path segment
// equals any segment of the current URL; deeper nodes (tab rows) win over
// sub-menus, which win over modules.
export const findPermissionForPath = (tree, pathname) => {
  if (!Array.isArray(tree) || !pathname) return null;
  const segs = String(pathname).split("?")[0].split("/").filter(Boolean).map((s) => s.toLowerCase());
  const key = (p) => String(p || "").split("?")[0].split("/").filter(Boolean)[0]?.toLowerCase();
  let best = null;
  const consider = (node, level) => {
    const k = key(node?.path);
    if (k && segs.includes(k) && (!best || level > best.level)) best = { node, level };
  };
  for (const m of tree) {
    consider(m, 1);
    for (const s of m.submodules || []) {
      consider(s, 2);
      for (const l of s.modulesOfSubModule || []) consider(l, 3);
    }
  }
  return best ? best.node : null;
};

// { permission, canApprove } for the page being rendered. When the page cannot
// be located in the tree, canApprove is true — the backend still enforces the
// Approve right on every approval route, the hook only hides what it can prove.
export default function useApproveRight() {
  const { userPermission } = useSelector(selectUser);
  const { pathname } = useLocation();
  return useMemo(() => {
    const permission = findPermissionForPath(userPermission, pathname);
    // Since 2026-09-17 Transfer / Reject / Revert / Punch are their own rights
    // on the sub-tab rows (actions), and a tab like Fund Transfer offers no
    // Approve flag at all. A role holding such an action was told "You do not
    // have the Approve right" before the request was even sent (2026-09-21).
    // Any granted action on the page's node or its sub-tabs counts; the backend
    // still checks the exact action / flag on every route.
    const anyAction = (n) =>
      Object.values(n?.actions || {}).some((v) => Number(v) === 1) || (n?.subTabs || []).some(anyAction);
    const canApprove = permission ? Number(permission.approve) === 1 || anyAction(permission) : true;
    return { permission, canApprove };
  }, [userPermission, pathname]);
}

export const APPROVE_DENIED_MESSAGE = "You do not have the Approve right for this screen";
